import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { t } from 'i18next';
import { format } from 'date-fns';
import QuestionsBlock from '../ReactComponent/QuestionsBlock';
import {
  Addresses,
  AddressLabel,
  AddressValue,
  Body,
  Content,
  DeliveryStatus,
  Header,
  PageContainer,
  PageTitleContainer,
  ProgressBar,
  ProgressBarTrack,
  ProgressContainer,
  ProgressStatus,
  ShipmentCreatedAt,
  ShipmentHeading,
  StatusContainer,
  Wrapper,
  Table,
  TableCell,
  CellLabel,
  CellValueLink,
  TableDivider,
} from './css';
import {
  DeliveryPhotoSection,
  DeliveryPhotoTitle,
  DeliveryPhotoInputRow,
  DeliveryPhotoPostalInputWrapper,
  DeliveryPhotoPostalInput,
  DeliveryPhotoError,
} from '../../lib/ui/table/css';
import getOrder from './api';
import { getShipmentTrackingDetailed } from '../../services/shipments';
import downloadDocument from '../ViewOrder/PODDocuments/api';
import ShipmentInfo from './ShipmentInfo';
import Tracking from '../../lib/ui/maps/Tracking';
import Breadcrumbs from '../../lib/ui/breadcrumbs';
import getOrderByBillNumber from './apiBillNumber';
import PrimaryButton from '../../lib/ui/buttons/PrimaryButton';

class ViewShipmentTracking extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      probillNumber: '',
      postalCode: '',
      postalError: '',
      safeDropPhotos: null,
      // data: mock
    };
  }

  componentDidMount() {
    const { accountNumbers } = this.props;
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      this.setState({
        probillNumber: urlParams.get('probillNumber'),
        billNumber: urlParams.get('billNumber'),
        division: urlParams.get('division'),
        orderId: urlParams.get('orderId'),
      }, () => {
        if (this.state.orderId) {
          getOrder(this.state.orderId, this.updateState, accountNumbers);
        } else if (this.state.probillNumber) {
          getShipmentTrackingDetailed({ division: this.state.division, probillNumber: this.state.probillNumber }).then((res) => {
            this.updateState('data', res.data);
          });
        } else if (this.state.billNumber) {
          getOrderByBillNumber(this.state.billNumber, []).then((res) => {
            this.updateState('data', res.data);
          });
        }
      });
    }
  }

  updateState = (name, value) => this.setState({ [name]: value });

  normalisePostal = (value) => (value || '').replace(/\s+/g, '').toUpperCase();

  handlePostalChange = (e) => {
    const { value } = e.target;
    this.setState({ postalCode: value, safeDropPhotos: null });

    if (!value) {
      this.setState({ postalError: '' });
      return;
    }

    const consigneePostal = this.state.data && this.state.data.to && this.state.data.to.postalCode;
    if (this.normalisePostal(value) !== this.normalisePostal(consigneePostal)) {
      this.setState({ postalError: t('PostalCodeError') });
    } else {
      this.setState({ postalError: '' });
    }
  };

  handleViewPhoto = () => {
    const documents = (this.state.data && this.state.data.documents) || [];
    const photos = documents.filter((doc) => doc.fileType === 'Safe_Drop_Photo');
    this.setState({ safeDropPhotos: photos });
  };

  filterTrackingAfterDelivered = (trackingData) => {
    if (!trackingData || trackingData.length === 0) {
      return trackingData;
    }

    // Find the index of the tracking object that contains "Delivered" status
    // Also track the latest delivered timestamp to handle multiple "delivered" statuses
    let deliveredTrackingIndex = -1;
    let deliveredTouchPointIndex = -1;
    let latestDeliveredTime = null;

    for (let i = 0; i < trackingData.length; i++) {
      const touchPoints = trackingData[i].touchPoints || [];
      for (let j = 0; j < touchPoints.length; j++) {
        const touchPoint = touchPoints[j];
        const status = touchPoint.status?.toLowerCase() || '';

        if (status.includes('delivered') || status.includes('livré') || status.includes('order canceled') || status.includes('commande annulée')) {
          // Get the timestamp for this delivered status
          const currentTime = touchPoint.time || touchPoint.date;

          // If this is the first "delivered" found, or if it has a later timestamp
          if (deliveredTrackingIndex === -1
            || (currentTime && (!latestDeliveredTime || new Date(currentTime) >= new Date(latestDeliveredTime)))) {
            deliveredTrackingIndex = i;
            deliveredTouchPointIndex = j;
            latestDeliveredTime = currentTime;
          }
        }
      }
    }

    // If no "Delivered" status found, return original data
    if (deliveredTrackingIndex === -1) {
      return trackingData;
    }

    // Filter: Keep only tracking objects up to and including the one with "Delivered"
    const filteredTracking = trackingData.slice(0, deliveredTrackingIndex + 1);

    // In the last tracking object, keep only touchpoints up to and including "Delivered"
    if (filteredTracking.length > 0) {
      const lastTracking = filteredTracking[filteredTracking.length - 1];
      lastTracking.touchPoints = lastTracking.touchPoints.slice(0, deliveredTouchPointIndex + 1);
    }

    return filteredTracking;
  };

  render() {
    const { isAuthenticated } = this.props;
    if (this.state.data) {
      const {
        tracking,
        trackingItems,
        shipmentNumber,
        createDate,
        deliveryDate,
        status,
        statusCode,
        statusPercentage,
        statusColor,
        estimatedDeliveryDate,
        probillNumber,
        billNumber,
        poNumber,
        shipperReferenceNumber,
        pallets,
        pieces,
        weight,
        totalWeight,
        from,
        to,
        billToAccountNumber,
        rlProbill,
        deliveryAppointment,
        deliverByEnd,
      } = this.state.data;

      const isExceptionStatus = (st, sc) => (/^Exception - Call Customer Service$/i.test(st)
        || /^Exception - Appeler le service à la clientèle/i.test(st)
        || /^Exception - OSD/i.test(st)
        || /^Livré - OSD/i.test(st))
        && sc === 'COMPLETE';

      const hasExceptionTouchPoint = (trackingData) => {
        if (!trackingData) return false;
        return trackingData.some((event) =>
          (event.touchPoints || []).some((tp) => isExceptionStatus(tp.status, tp.statusCode)));
      };

      const exceptionStatus = isExceptionStatus(status, statusCode)
        || hasExceptionTouchPoint(tracking)
        || (trackingItems && trackingItems.some((tp) => isExceptionStatus(tp.status, tp.statusCode)));

      const shipmentInfo = {
        billNumber,
        probillNumber,
        poNumber,
        shipperReferenceNumber,
        rlProbill,
        pallets,
        pieces,
        totalWeight: `${weight ? weight.value : totalWeight.value} ${weight ? weight.unitOfMeasure : totalWeight.unitOfMeasure}`,
        deliveryAppointment,
        deliverByEnd,
      };

      let trackingObject;

      if (trackingItems) {
        trackingObject = [{
          date: deliveryDate ?? '',
          id: 1,
          touchPoints: trackingItems.map((touchPoint, index) => {
            const tpException = isExceptionStatus(touchPoint.status, touchPoint.statusCode);
            return {
              id: index,
              time: touchPoint.date ?? '',
              status: tpException ? t('ExceptionStatus') : touchPoint.status,
              statusCode: touchPoint.statusCode,
              location: tpException ? null : {
                city: touchPoint.city,
                state: touchPoint.provinceCode,
                country: touchPoint.countryCode,
              },
              coords: {
                latitude: touchPoint.latitude,
                longitude: touchPoint.longitude,
              },
            };
          }),
        }];
      }

      // Filter tracking data to remove statuses after "Delivered"
      const filteredTracking = this.filterTrackingAfterDelivered(tracking || trackingObject);

      const estimateMsg = [
        { id: '0000130902', message: t('EstimateMsgSignatureRequired') },
        { id: '0000110546', message: t('EstimateMsgDeliveryAppointment') },
      ];
      const estimateMsgIdx = estimateMsg.map((obj) => obj.id).indexOf(billToAccountNumber);

      return (
        <Wrapper>
          <div className="container">
            <PageContainer>
              <Breadcrumbs
                nopadding="no-padding-left"
                isAuthenticated={isAuthenticated}
                items={[t('Track Shipments'), `${t('Shipment')} ${shipmentNumber || billNumber}`]}
              />
              <Header>
                <PageTitleContainer>
                  <ShipmentHeading>
                    {t('TrackingDetails')}
                    :
                    {' '}
                    {shipmentNumber || billNumber}
                  </ShipmentHeading>
                  <ShipmentCreatedAt>
                    {t('CreatedIn')}
                    {' '}
                    {createDate ? format(createDate, t('DATE_FORMAT_PATTERN')) : format(new Date(), t('DATE_FORMAT_PATTERN'))}
                  </ShipmentCreatedAt>
                </PageTitleContainer>

                <StatusContainer>
                  <ProgressContainer>
                    <ProgressBar>
                      <ProgressBarTrack progress={statusPercentage} color={statusColor} />
                    </ProgressBar>
                    <ProgressStatus>{t(status)}</ProgressStatus>
                  </ProgressContainer>

                  <DeliveryStatus>
                    {(() => {
                      if (exceptionStatus) {
                        return (
                          <div>
                            {t('EstDelivery')}
                            :
                          </div>
                        );
                      }

                      if (estimateMsgIdx > -1) {
                        return <div>{estimateMsg[estimateMsgIdx].message}</div>;
                      }

                      if (deliveryDate) {
                        return (
                          <div>
                            {t('EstDelivery')}
                            :
                            {' '}
                            <br />
                            <strong>
                              {format(deliveryDate, t('DATE_DELIVERY_PATTERN'))}
                            </strong>
                          </div>
                        );
                      }

                      return (
                        <div>
                          {t('EstDelivery')}
                          :
                          {' '}
                          <br />
                          <strong>
                            {(deliveryAppointment || estimatedDeliveryDate)
                              ? format(deliveryAppointment || estimatedDeliveryDate, t('DATE_FORMAT_PATTERN'))
                              : ''}
                          </strong>
                        </div>
                      );
                    })()}
                  </DeliveryStatus>
                </StatusContainer>
              </Header>

              <Body>
                <ShipmentInfo data={shipmentInfo} />
                <Content>
                  <Addresses>
                    <AddressLabel>{t('From')}</AddressLabel>
                    <AddressValue>
                      {from.city}
                      ,
                      {' '}
                      {from.provinceCode}
                      <br />
                      {from.countryCode}
                    </AddressValue>
                    <AddressLabel>{t('To')}</AddressLabel>
                    <AddressValue>
                      {to.city}
                      ,
                      {' '}
                      {to.provinceCode}
                      <br />
                      {to.countryCode}
                    </AddressValue>
                  </Addresses>
                  <Tracking data={filteredTracking} />

                  <DeliveryPhotoSection>
                    <DeliveryPhotoTitle>{t('CheckForDeliveryPhoto')}</DeliveryPhotoTitle>
                    <DeliveryPhotoInputRow>
                      <DeliveryPhotoPostalInputWrapper>
                        <DeliveryPhotoPostalInput
                          type="text"
                          placeholder={t('ConsigneePostalCode')}
                          value={this.state.postalCode}
                          onChange={this.handlePostalChange}
                          hasError={!!this.state.postalError}
                          aria-label={t('ConsigneePostalCode')}
                        />
                        {this.state.postalError && (
                          <DeliveryPhotoError>{this.state.postalError}</DeliveryPhotoError>
                        )}
                      </DeliveryPhotoPostalInputWrapper>
                      <PrimaryButton
                        type="button"
                        disabled={
                          !this.state.postalCode
                          || this.normalisePostal(this.state.postalCode) !== this.normalisePostal(to && to.postalCode)
                        }
                        onClick={this.handleViewPhoto}
                      >
                        {t('ViewPhoto')}
                      </PrimaryButton>
                    </DeliveryPhotoInputRow>

                    {this.state.safeDropPhotos && this.state.safeDropPhotos.length > 0 && (
                      <Table>
                        {this.state.safeDropPhotos.map(({ documentId, fileType }) => (
                          <React.Fragment key={documentId}>
                            <TableCell>
                              <CellLabel>{fileType}</CellLabel>
                              <CellValueLink
                                onClick={(e) => {
                                  e.preventDefault();
                                  downloadDocument(documentId, null, documentId);
                                }}
                              >
                                {t('View')}
                              </CellValueLink>
                            </TableCell>
                            <TableDivider />
                          </React.Fragment>
                        ))}
                      </Table>
                    )}

                    {this.state.safeDropPhotos && this.state.safeDropPhotos.length === 0 && (
                      <DeliveryPhotoError>{t('NoPhotoAvailable')}</DeliveryPhotoError>
                    )}
                  </DeliveryPhotoSection>
                </Content>
              </Body>

            </PageContainer>
            <QuestionsBlock />
          </div>
        </Wrapper>
      );
    }

    return <div />;
  }
}

ViewShipmentTracking.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  accountNumbers: PropTypes.array.isRequired,
};

const mstp = (state) => ({
  serviceLevelQuotes: state.user.serviceLevelQuotes,
  isAuthenticated: state.profile.isAuthenticated,
  accountNumbers: state.profile.accountNumbers,
  division: state.profile.division,
  userId: state.profile.userId,
  userEmail: state.profile.email,
  billingAccounts: state.profile.billingAccounts,
});

export default connect(mstp)(ViewShipmentTracking);
